/**
 * PHSIMS Web Admin - Password Reset Page
 * Author: Iswe Solutions Team
 * 
 * Public page for resetting password via token (from invite email).
 * Matches the PHSIMS login page design.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiClient } from '../services/api';
import { AppFooter } from './common/AppFooter';

const PasswordResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Validate token exists
  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please use the link from your invite email.');
    }
  }, [token]);

  // Calculate password strength
  useEffect(() => {
    let strength = 0;
    if (password.length >= 12) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 12.5;
    if (/[^A-Za-z0-9]/.test(password)) strength += 12.5;
    setPasswordStrength(strength);
  }, [password]);

  const getStrengthColor = () => {
    if (passwordStrength < 50) return '#d63031';
    if (passwordStrength < 75) return '#fd7e14';
    if (passwordStrength < 100) return '#fdcb6e';
    return '#00b894';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 50) return 'Weak';
    if (passwordStrength < 75) return 'Fair';
    if (passwordStrength < 100) return 'Good';
    return 'Strong';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (passwordStrength < 100) {
      setError('Password does not meet all requirements');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await apiClient.post('/auth/reset-password-with-token', {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err: unknown) {
      setLoading(false);
      // apiClient throws plain Error with extracted message from backend response
      const message = err instanceof Error ? err.message : '';
      if (message && !message.startsWith('API Error:')) {
        setError(message);
      } else if (message) {
        // Strip "API Error: 400 Bad Request" prefix for cleaner display
        setError('Failed to reset password. Please check your password meets all requirements and try again.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    }
  };

  // Success state
  if (success) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: '#0d3322',
          position: 'fixed',
          top: 0,
          left: 0,
        }}
      >
        <Box
          sx={{
            background: 'white',
            padding: { xs: '24px 20px', sm: '32px 40px' },
            borderRadius: { xs: '16px', sm: '20px' },
            boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
            width: { xs: '100%', sm: 460 },
            maxWidth: { xs: '94%', sm: '94%' },
            borderTop: '5px solid #ef7d00',
            textAlign: 'center',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 64, color: '#00b894', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#0d5c2e' }}>
            Password Set Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your password has been set. You can now log in with your credentials.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/login')}
            sx={{
              py: 1.5,
              backgroundColor: '#198a4b',
              '&:hover': { backgroundColor: '#146c43' },
              borderRadius: '10px',
              fontWeight: 600,
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#0d3322',
        position: 'fixed',
        top: 0,
        left: 0,
      }}
    >
      <Box
        sx={{
          background: 'white',
          padding: { xs: '24px 20px', sm: '32px 40px' },
          borderRadius: { xs: '16px', sm: '20px' },
          boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
          width: { xs: '100%', sm: 460 },
          maxWidth: { xs: '94%', sm: '94%' },
          borderTop: '5px solid #ef7d00',
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2.5 } }}>
          <Box
            component="img"
            src="/znphi_logo.jpg"
            alt="ZNPHI Logo"
            sx={{
              width: { xs: 60, sm: 70 },
              height: 'auto',
              borderRadius: '8px',
              mb: 1,
              objectFit: 'contain',
            }}
          />
          <Typography
            sx={{
              color: '#0d5c2e',
              fontSize: { xs: '14px', sm: '16px' },
              fontWeight: 800,
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            Public Health Security &<br />Information Management System
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '20px', sm: '24px' },
              fontWeight: 800,
              letterSpacing: '2px',
              color: '#ef7d00',
            }}
          >
            PHSIMS
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Set Your Password
          </Typography>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: '10px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              fontSize: '13px',
              py: 0.5,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading || !token}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  fontWeight: 500,
                  '& fieldset': { borderWidth: '2px', borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)' },
                },
                '& .MuiOutlinedInput-input': { padding: '12px 12px 12px 0', fontSize: '14px' },
              }}
            />
            
            {/* Password strength indicator */}
            {password && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Password strength
                  </Typography>
                  <Typography variant="caption" sx={{ color: getStrengthColor(), fontWeight: 600 }}>
                    {getStrengthLabel()}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { backgroundColor: getStrengthColor(), borderRadius: 3 },
                  }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading || !token}
              size="small"
              error={confirmPassword !== '' && password !== confirmPassword}
              helperText={confirmPassword !== '' && password !== confirmPassword ? 'Passwords do not match' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  fontWeight: 500,
                  '& fieldset': { borderWidth: '2px', borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused fieldset': { borderColor: '#198a4b' },
                  '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)' },
                },
                '& .MuiOutlinedInput-input': { padding: '12px 12px 12px 0', fontSize: '14px' },
              }}
            />

            {/* Password requirements */}
            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom sx={{ color: '#0d5c2e' }}>
                Password Requirements:
              </Typography>
              <Typography variant="caption" component="ul" sx={{ pl: 2, m: 0, lineHeight: 1.6 }}>
                <li style={{ color: password.length >= 12 ? '#00b894' : '#666' }}>At least 12 characters</li>
                <li style={{ color: /[A-Z]/.test(password) ? '#00b894' : '#666' }}>One uppercase letter</li>
                <li style={{ color: /[a-z]/.test(password) ? '#00b894' : '#666' }}>One lowercase letter</li>
                <li style={{ color: /[0-9]/.test(password) ? '#00b894' : '#666' }}>One number</li>
                <li style={{ color: /[^A-Za-z0-9]/.test(password) ? '#00b894' : '#666' }}>One special character</li>
              </Typography>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !token || passwordStrength < 100 || password !== confirmPassword}
              sx={{
                py: 1.5,
                mt: 0.5,
                backgroundColor: '#198a4b',
                '&:hover': { backgroundColor: '#146c43' },
                '&:disabled': { backgroundColor: '#ccc' },
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '15px',
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Set Password'}
            </Button>
          </Box>
        </form>

        {/* Footer */}
        <AppFooter variant="auth" />
      </Box>
    </Box>
  );
};

export default PasswordResetPage;
